// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { Tag } from "@quartzds/core";
import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
} from "@stencil/core";

import { randomId } from "../utils";

export type Layer = "main" | "panel" | "popup";
export type Level = "section" | "subsection";

const accordionElementName = "qds-web-accordion";

const isCounter = (value: unknown): value is number =>
  typeof value === "string"
    ? !Number.isNaN(Number.parseFloat(value))
    : typeof value === "number";

@Component({
  tag: "qds-web-accordion-item",
  shadow: true,
  styleUrl: "accordion-item.css",
})
export class AccordionItem implements ComponentInterface {
  @Element() public el: HTMLElement;

  /**
   * Displays a counter badge if a number or string representing a number is used.
   */
  @Prop() public readonly counter?: number | string;

  /**
   * The counter badge's accessible text that will be used by
   * screen readers.
   *
   * @example
   * <qds-web-accordion-item counter="2" counter-description="new notifications"></qds-web-accordion-item>
   * // This accordion item's counter will be read by screen readers as "2 new notifications"
   */

  @Prop() public readonly counterDescription?: string;

  /**
   * The accordion item's title.
   */
  @Prop() public readonly text: string;

  /**
   * Accordion initial state
   */

  @Prop() public isExpanded = false;

  /**
   * Control whether to display the bottom divider line or not.
   *
   * By default, the divider is visible and uses the full width of the accordion item.
   *
   * Set the divider value to `false` to hide it completely.
   *
   */
  @Prop() public readonly divider? = true;

  @State() public layer?: Layer = "main";

  @State() public level?: Level = "section";

  @State() public single?: boolean = false;

  @State() public accordionItemControlledId: string;

  @State() public tag?: Tag;

  @State() public isCurrentItem: boolean;

  /**
   * Collapses the accordion items which are not already expanded
   */
  @Event({ eventName: "qds-accordion-item:collapse" })
  public collapseAccordionItems: EventEmitter<{ parent: HTMLElement | null }>;

  public detailsElement: HTMLElement | undefined = undefined;

  @Listen("resize", { target: "window" })
  public handleResize(): void {
    if (this.detailsElement) {
      this.detailsElement.style.maxHeight = this.getMaxHeight();
    }
  }

  @Listen("qds-accordion-event", { target: "document" })
  public handleAccordionEvent(): void {
    this.setAccordionAttributes();
  }

  @Listen("qds-accordion-item:collapse", { target: "document" })
  public handleCollapseAccordionItemsEvent(
    event: CustomEvent<{ parent: HTMLElement }>,
  ): void {
    const parentElement = this.el.closest(accordionElementName);

    if (parentElement === event.detail.parent) {
      if (!this.isCurrentItem) this.isExpanded = false;
      this.isCurrentItem = false;
    }
  }

  public componentDidLoad(): void {
    this.setAccordionAttributes();
  }

  public setAccordionAttributes(): void {
    const accordionElement = this.el.closest(accordionElementName);
    if (!accordionElement) return;

    this.layer = (accordionElement.layer ?? "main") as Layer;
    this.level = (accordionElement.level ?? "section") as Level;
    this.tag = (accordionElement.tag ?? "h3") as Tag;
    this.single = accordionElement.single;
  }

  public toggleAccordion(): void {
    if (this.single !== undefined && this.single) {
      this.isCurrentItem = true;
      const parentAccordion = this.el.closest(accordionElementName);

      this.collapseAccordionItems.emit({ parent: parentAccordion });
    }
    this.isExpanded = !this.isExpanded;
  }

  public componentWillLoad(): void {
    this.accordionItemControlledId = randomId();
  }

  public render() {
    const Tag = this.tag ?? "h3";

    return (
      <div
        class={{
          "divider-wrapper": true,
          "divider-gap": this.divider !== undefined && this.divider,
        }}
        data-layer={this.layer}
        data-level={this.level}
      >
        <div class="accordion-item">
          <Tag class={{ header: true, "header-gap": this.isExpanded }}>
            <button
              type="button"
              onClick={() => {
                this.toggleAccordion();
              }}
              aria-expanded={this.isExpanded ? "true" : "false"}
              aria-controls={`accordion-item-${this.accordionItemControlledId}`}
            >
              <div class="title">{this.text}</div>
              {isCounter(this.counter) && (
                <qds-badge-counter
                  description={this.counterDescription}
                  strokeRing
                  value={this.counter}
                />
              )}
              <div class="action-button">
                <qds-icon
                  aria-hidden="true"
                  library="core"
                  name="dropdown"
                  class={{
                    "open-icon": this.isExpanded,
                  }}
                />
              </div>
            </button>
          </Tag>
          <div
            id={`accordion-item-${this.accordionItemControlledId}`}
            class={{
              details: true,
              "details-padding": true,
              "details-open": this.isExpanded,
            }}
            style={{
              maxHeight: this.getMaxHeight(),
            }}
            ref={(element) => {
              this.detailsElement = element as HTMLElement;
            }}
            aria-hidden={this.isExpanded ? "false" : "true"}
            // makes all descendants non-interactive
            inert={!this.isExpanded}
          >
            <slot />
          </div>
        </div>
        {this.divider && <qds-divider />}
      </div>
    );
  }

  private getMaxHeight(): string {
    if (this.isExpanded) {
      return this.detailsElement?.scrollHeight === undefined
        ? "900px"
        : `${this.detailsElement.scrollHeight}px`;
    }
    return "0px";
  }
}
